/**
 * Influence — le classement, et ce qui le fait bouger.
 *
 * Un score nu ne sert à rien : chaque axe s'ouvre sur ses composantes, avec ce
 * qui a été pris en compte et d'où ça vient (déclaré ou mesuré par la
 * plateforme). Et la première chose visible après le chiffre, c'est ce qui le
 * ferait monter.
 *
 * Les questions sensibles (engagement associatif) sont posées comme Tom les a
 * formulées, sans jamais juger la réponse : « non » est une réponse, pas une
 * faute, et l'écran le dit.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Copy, Gift, Link2, TrendingUp, Trophy, Users, X } from 'lucide-react';
import { appelerApi, envoyer } from '../../reseau.ts';
import { Badge, Bar, Btn, Card, Input, Modal, PageTitle, SectionLabel, Select, Table } from '../../admin/ui.tsx';
import {
  LIBELLE_ROLE,
  PORTEES_ENGAGEMENT,
  RESEAUX_SOCIAUX,
  ROLES_ENGAGEMENT,
  dateCourte,
  euros,
  nomDe,
  type AudienceSociale,
  type AxeInfluence,
  type ClassementInfluence,
  type Engagement,
  type MonInfluence,
  type MonParrainage,
  type Tableau,
  type Tableaux,
} from './types.ts';
import { EncartParrainage } from './EncartParrainage.tsx';
import { Etoile } from '../../composants/Etoile.tsx';

export function Influence() {
  const [influence, setInfluence] = useState<MonInfluence | null>(null);
  const [classement, setClassement] = useState<ClassementInfluence | null>(null);
  const [parrainage, setParrainage] = useState<MonParrainage | null>(null);
  const [tableaux, setTableaux] = useState<Tableaux | null>(null);

  const charger = useCallback(async () => {
    const [i, c, p, t] = await Promise.all([
      appelerApi<MonInfluence>('/api/influence/moi'),
      appelerApi<ClassementInfluence>('/api/influence/classement?limite=10'),
      appelerApi<MonParrainage>('/api/parrainage/moi'),
      appelerApi<Tableaux>('/api/influence/tableaux?limite=10'),
    ]);
    setInfluence(i.donnees);
    setClassement(c.donnees);
    setParrainage(p.donnees);
    setTableaux(t.donnees);
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  // Un levier cliqué ailleurs arrive ici avec une ancre : on y descend.
  useEffect(() => {
    if (!influence || !location.hash) return;
    document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [influence]);

  if (!influence) return <p className="text-[14px] text-fumee">Chargement…</p>;

  return (
    <>
      <PageTitle
        title={
          <>
            Votre <em className="text-match-gradient font-serif italic font-medium">influence</em>
          </>
        }
        subtitle="Ce que vous pesez vraiment dans le réseau : votre entreprise, les relations que vous créez, l’audience que vous portez."
      />

      <div className="mb-6 grid gap-4 lg:grid-cols-3">
        <ScoreEtLeviers influence={influence} />
        <TableauCourt titre="Trending" sous="Les meilleures progressions cette semaine" tableau={tableaux?.trending ?? null} unite="pts" cle="trending" />
        <TableauCourt titre="All time" sous="Les profils les plus influents de la plateforme" tableau={tableaux?.all_time ?? null} unite="" cle="all_time" />
      </div>

      <div className="mb-6 grid gap-4 lg:grid-cols-3">
        <Axe titre="Influence sociale" axe={influence.sociale} note="Votre entreprise d’abord ; l’engagement compte, mais il est plafonné." />
        <Axe titre="Influence réseau" axe={influence.reseau} note="Le cœur du métier : clients servis, gens amenés ici, rencontres qui aboutissent." />
        <Axe titre="Influence virtuelle" axe={influence.virtuelle} note="Vos audiences, et l’attention que vous récoltez réellement ici." />
      </div>

      <Declarations influence={influence} onChange={charger} />
      <Parrainage parrainage={parrainage} />
      <Classement classement={classement} onChanger={setClassement} secteur={influence.rang.secteur} />
    </>
  );
}

/* ————————————————————————————————————————————————————————————— */

function ScoreEtLeviers({ influence }: { influence: MonInfluence }) {
  const delta = influence.progression.depuis_une_semaine;
  return (
    <Card className="flex flex-col">
      <SectionLabel>Score d’influence</SectionLabel>
      <p className="mt-2 flex items-baseline gap-3">
        <span className="text-match-gradient font-serif text-[52px] leading-none">{influence.global}</span>
        <span className="text-[14px] text-fumee">/ 1000</span>
      </p>
      <p className="mt-1.5 flex flex-wrap items-center gap-x-3 text-[14px] text-blanc">
        {influence.palier}
        {influence.boost_premium > 0 && <Etoile premium />}
        {influence.boost_premium > 0 && <span className="text-[13px] text-fumee">dont +{influence.boost_premium} Premium</span>}
        <span className="flex items-center gap-1 text-[13px] text-fumee">
          {delta === null ? (
            'première mesure'
          ) : (
            <>
              <TrendingUp size={13} strokeWidth={1.5} className={delta >= 0 ? '' : 'rotate-180'} />
              {delta >= 0 ? `+${delta}` : delta} cette semaine
            </>
          )}
        </span>
      </p>
      <p className="mt-1 text-[13px] text-fumee">
        Moi : {influence.rang.rang}<sup>e</sup> sur {influence.rang.total}
        {influence.rang.dans_mon_secteur && influence.rang.secteur
          ? ` · ${influence.rang.dans_mon_secteur.rang}e en ${influence.rang.secteur}`
          : ''}
      </p>

      <div className="mt-5 flex-1 border-t border-graphite pt-4">
        <SectionLabel>Ce qui vous ferait monter</SectionLabel>
        {influence.leviers.length === 0 ? (
          <p className="mt-2 text-[13px] text-fumee">Tout est renseigné — il ne reste qu’à agir : parrainer, présenter, prendre rendez-vous.</p>
        ) : (
          <div className="mt-2 flex flex-col">
            {influence.leviers.map((l) => (
              <Link
                key={l.quoi}
                to={l.cible}
                onClick={() => {
                  // Même page : le routeur ne rechargera pas, on descend nous-mêmes.
                  const [chemin, ancre] = l.cible.split('#');
                  if (chemin === location.pathname && ancre) {
                    setTimeout(() => document.getElementById(ancre)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
                  }
                }}
                className="group flex items-center justify-between gap-3 border-b border-graphite py-2 last:border-0 last:pb-0"
              >
                <span className="text-[13px] text-blanc group-hover:underline">{l.quoi}</span>
                <span className="text-match-gradient flex shrink-0 items-center gap-1 text-[13px]">
                  +{l.gain} <ArrowRight size={12} strokeWidth={1.5} className="text-fumee group-hover:text-blanc" />
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

function LigneRang({ r, unite }: { r: Tableau['top'][number]; unite: string }) {
  return (
    <div className={`flex items-center gap-3 border-b border-graphite py-2 last:border-0 ${r.moi ? 'rounded-[6px] bg-[#262626] px-2' : ''}`}>
      <span className={`w-6 shrink-0 font-serif text-[16px] ${r.rang <= 3 ? 'text-match-gradient' : 'text-fumee'}`}>{r.rang}</span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] text-blanc">
          {r.entreprise ?? nomDe({ prenom: r.prenom, nom: r.nom })} <Etoile premium={r.premium} texte={false} />
          {r.moi && <span className="ml-1.5 text-[11px] text-fumee">vous</span>}
        </p>
        <p className="truncate text-[11px] text-fumee">{r.secteur ?? '—'}</p>
      </div>
      <span className="shrink-0 text-[14px] text-blanc">
        {unite ? `+${r.valeur} ${unite}` : r.valeur}
      </span>
    </div>
  );
}

function TableauCourt({ titre, sous, tableau, unite, cle }: { titre: string; sous: string; tableau: Tableau | null; unite: string; cle: 'trending' | 'all_time' }) {
  const [tout, setTout] = useState<Tableau | null>(null);
  const ouvrirTout = async () => {
    const r = await appelerApi<Tableaux>('/api/influence/tableaux?limite=200');
    setTout(r.donnees?.[cle] ?? null);
  };
  return (
    <Card className="flex flex-col">
      <div className="flex items-start justify-between gap-3">
        <div>
          <SectionLabel>{titre}</SectionLabel>
          <p className="mt-1 text-[12px] text-fumee">{sous}</p>
        </div>
        {titre === 'All time' && <Trophy size={16} strokeWidth={1.5} className="text-fumee" />}
        {titre === 'Trending' && <TrendingUp size={16} strokeWidth={1.5} className="text-fumee" />}
      </div>
      <div className="mt-3 flex-1">
        {!tableau && <p className="text-[13px] text-fumee">Chargement…</p>}
        {tableau?.top.length === 0 && <p className="text-[13px] text-fumee">Personne pour l’instant — le classement se remplit chaque jour.</p>}
        {tableau?.top.map((r) => <LigneRang key={r.personne_id} r={r} unite={unite} />)}
      </div>
      {tableau && (
        <div className="mt-3 flex items-center justify-between gap-3 border-t border-graphite pt-3">
          <span className="text-[13px] text-fumee">
            {tableau.moi ? <>Moi : <span className="text-blanc">{tableau.moi.rang}<sup>e</sup></span> sur {tableau.total}</> : 'Moi : pas encore classé'}
          </span>
          {tableau.total > tableau.top.length && (
            <button onClick={() => void ouvrirTout()} className="text-[13px] text-fumee underline hover:text-blanc">Afficher tout</button>
          )}
        </div>
      )}
      {tout && (
        <Modal title={`${titre} — ${tout.total} profils`} onClose={() => setTout(null)}>
          <div className="flex flex-col">
            {tout.top.map((r) => <LigneRang key={r.personne_id} r={r} unite={unite} />)}
          </div>
        </Modal>
      )}
    </Card>
  );
}

function Axe({ titre, axe, note }: { titre: string; axe: AxeInfluence; note: string }) {
  return (
    <Card className="flex flex-col">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-[15px] text-blanc">{titre}</p>
        <span className="font-serif text-[26px] leading-none text-blanc">{axe.score}</span>
      </div>
      <div className="mt-3">
        <Bar value={axe.score} max={1000} />
      </div>
      <p className="mt-3 text-[13px] leading-relaxed text-fumee">{note}</p>

      <div className="mt-4 flex flex-col gap-3 border-t border-graphite pt-4">
        {axe.composantes.map((c) => (
          <div key={c.libelle}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[13px] text-blanc">{c.libelle}</span>
              <span className="text-[12px] text-fumee">{Math.round(c.valeur * 100)} %</span>
            </div>
            <p className="mt-0.5 text-[12px] text-fumee">
              {c.detail}
              {c.source === 'declare' && <span className="text-fumee/60"> · déclaré</span>}
            </p>
          </div>
        ))}
      </div>
    </Card>
  );
}

/* ————————————————— Ce que je déclare ————————————————— */

function Declarations({ influence, onChange }: { influence: MonInfluence; onChange: () => Promise<void> }) {
  const d = influence.declarations;
  const [employes, setEmployes] = useState(d.nbEmployes?.toString() ?? '');
  const [clientsAn, setClientsAn] = useState(d.nbClientsAnnuel?.toString() ?? '');
  const [clientsBase, setClientsBase] = useState(d.nbClientsBase?.toString() ?? '');
  const [engage, setEngage] = useState<boolean | null>(d.engagementDeclare);
  const [engagements, setEngagements] = useState<Engagement[]>(d.engagements);
  const [audiences, setAudiences] = useState<AudienceSociale[]>(d.audiences);
  const [etat, setEtat] = useState<'repos' | 'envoi' | 'ok'>('repos');

  const nombreOuNul = (v: string) => (v.trim() === '' ? null : Math.max(0, Math.round(Number(v) || 0)));

  const enregistrer = async () => {
    setEtat('envoi');
    await envoyer(
      '/api/influence/moi',
      {
        nbEmployes: nombreOuNul(employes),
        nbClientsAnnuel: nombreOuNul(clientsAn),
        nbClientsBase: nombreOuNul(clientsBase),
        engagementDeclare: engage,
        engagements: engage ? engagements.filter((e) => e.nom.trim()) : [],
        audiences: audiences.filter((a) => a.reseau.trim()),
      },
      'PUT',
    );
    await onChange();
    setEtat('ok');
    setTimeout(() => setEtat('repos'), 2500);
  };

  return (
    <Card className="mb-6">
      <span id="chiffres" className="block scroll-mt-6" />
      <SectionLabel>Vos chiffres</SectionLabel>
      <p className="mt-2 max-w-[680px] text-[13px] leading-relaxed text-fumee">
        Tout est facultatif. Ce que vous déclarez compte un peu moins que ce que la plateforme
        constate elle-même — c’est normal, et c’est dit sous chaque composante.
      </p>

      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <Input label="Personnes dans l’entreprise" value={employes} onChange={setEmployes} placeholder="12" type="number" />
        <Input label="Clients servis par an" value={clientsAn} onChange={setClientsAn} placeholder="180" type="number" />
        <Input label="Clients dans votre base" value={clientsBase} onChange={setClientsBase} placeholder="1400" type="number" />
      </div>
      <p className="mt-2 text-[12px] text-fumee">
        Le chiffre d’affaires se règle dans « Moi et mon entreprise » — en tranche, jamais au chiffre près.
      </p>

      {/* L'engagement, demandé comme Tom l'a formulé. */}
      <div className="mt-7 border-t border-graphite pt-5">
        <p className="text-[15px] text-blanc">
          Soutenez-vous une cause, une organisation, ou peut-on vous rencontrer dans une activité
          avec des bénévoles quelque part ?
        </p>
        <p className="mt-1.5 text-[13px] leading-relaxed text-fumee">
          Église, club, association, mission, conseil d’administration… Répondre « non » ne vous
          retire aucun point : cette partie ne peut apporter que 15 % de l’axe social, votre
          entreprise fait le reste.
        </p>

        <div className="mt-3 flex flex-wrap gap-2">
          <Btn variant={engage === true ? 'primary' : 'ghost'} onClick={() => setEngage(true)}>
            Oui, au moins une
          </Btn>
          <Btn variant={engage === false ? 'primary' : 'ghost'} onClick={() => setEngage(false)}>
            Non, pas pour l’instant
          </Btn>
        </div>

        {engage === true && (
          <div className="mt-4 flex flex-col gap-3">
            {engagements.map((e, i) => (
              <div key={i} className="grid items-end gap-3 sm:grid-cols-[1fr_auto_auto_auto]">
                <Input
                  label={i === 0 ? 'Laquelle ?' : ''}
                  value={e.nom}
                  onChange={(v) => setEngagements(engagements.map((x, j) => (j === i ? { ...x, nom: v } : x)))}
                  placeholder="Église CCB"
                />
                <Select
                  label={i === 0 ? 'Votre rôle' : ''}
                  value={e.role ?? 'benevole'}
                  onChange={(v) => setEngagements(engagements.map((x, j) => (j === i ? { ...x, role: v as never } : x)))}
                  options={ROLES_ENGAGEMENT}
                />
                <Select
                  label={i === 0 ? 'Portée' : ''}
                  value={e.portee ?? 'locale'}
                  onChange={(v) => setEngagements(engagements.map((x, j) => (j === i ? { ...x, portee: v as never } : x)))}
                  options={PORTEES_ENGAGEMENT}
                />
                <button
                  onClick={() => setEngagements(engagements.filter((_, j) => j !== i))}
                  aria-label="Retirer"
                  className="mb-2.5 text-fumee hover:text-blanc"
                >
                  <X size={16} strokeWidth={1.5} />
                </button>
              </div>
            ))}
            {engagements.length < 8 && (
              <Btn onClick={() => setEngagements([...engagements, { nom: '', role: 'benevole', portee: 'locale' }])}>
                Ajouter une cause
              </Btn>
            )}
          </div>
        )}
      </div>

      {/* Les audiences */}
      <div className="mt-7 border-t border-graphite pt-5">
        <span id="audiences" className="block scroll-mt-6" />
        <p className="text-[15px] text-blanc">Vos audiences</p>
        <p className="mt-1.5 text-[13px] leading-relaxed text-fumee">
          Le lien compte autant que le chiffre : il permet à ceux qui vous découvrent d’aller voir
          ce que vous publiez. Une audience sans lien ne vaut que la moitié.
        </p>

        <div className="mt-3 flex flex-col gap-3">
          {audiences.map((a, i) => (
            <div key={i} className="grid items-end gap-3 sm:grid-cols-[auto_1fr_auto_auto]">
              <Select
                label={i === 0 ? 'Réseau' : ''}
                value={a.reseau}
                onChange={(v) => setAudiences(audiences.map((x, j) => (j === i ? { ...x, reseau: v } : x)))}
                options={RESEAUX_SOCIAUX.map((r) => ({ valeur: r, texte: r }))}
              />
              <Input
                label={i === 0 ? 'Lien vers votre page' : ''}
                value={a.url ?? ''}
                onChange={(v) => setAudiences(audiences.map((x, j) => (j === i ? { ...x, url: v } : x)))}
                placeholder="https://www.linkedin.com/in/…"
              />
              <Input
                label={i === 0 ? 'Abonnés' : ''}
                value={a.abonnes?.toString() ?? ''}
                onChange={(v) => setAudiences(audiences.map((x, j) => (j === i ? { ...x, abonnes: Number(v) || null } : x)))}
                placeholder="4200"
                type="number"
              />
              <button
                onClick={() => setAudiences(audiences.filter((_, j) => j !== i))}
                aria-label="Retirer"
                className="mb-2.5 text-fumee hover:text-blanc"
              >
                <X size={16} strokeWidth={1.5} />
              </button>
            </div>
          ))}
          {audiences.length < 10 && (
            <Btn onClick={() => setAudiences([...audiences, { reseau: 'LinkedIn', url: '', abonnes: null }])}>
              Ajouter un réseau
            </Btn>
          )}
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3">
        <Btn variant="primary" onClick={() => void enregistrer()} disabled={etat === 'envoi'}>
          {etat === 'envoi' ? 'Enregistrement…' : 'Enregistrer'}
        </Btn>
        {etat === 'ok' && (
          <span className="flex items-center gap-1.5 text-[13px] text-blanc">
            <Check size={14} strokeWidth={1.5} /> Score recalculé
          </span>
        )}
      </div>
    </Card>
  );
}

/* ————————————————— Parrainage ————————————————— */

function Parrainage({ parrainage }: { parrainage: MonParrainage | null }) {
  const [copie, setCopie] = useState(false);
  if (!parrainage) return null;

  const copier = async () => {
    await navigator.clipboard.writeText(parrainage.url).catch(() => undefined);
    setCopie(true);
    setTimeout(() => setCopie(false), 2500);
  };

  const abonnes = parrainage.filleuls.filter((f) => f.abonne).length;

  return (
    <Card className="mb-6">
      <span id="parrainage" className="block scroll-mt-6" />
      <SectionLabel>Votre lien de parrainage</SectionLabel>
      <div className="mt-3">
        <EncartParrainage compact />
      </div>
      <p className="mt-2 max-w-[680px] text-[13px] leading-relaxed text-fumee">
        Chaque entrepreneur qui s’inscrit par ce lien — ou via un de vos événements — compte dans
        votre influence réseau, et vous touchez 5 % de tout ce qu’il dépensera ici, aussi longtemps
        qu’il paie ; 2 % sur les dépenses de ses propres filleuls.
      </p>

      <div className="mt-4 flex flex-wrap items-start gap-6">
        {parrainage.qr && (
          <img
            src={parrainage.qr}
            alt="QR code de votre lien de parrainage"
            className="h-32 w-32 rounded-[8px] bg-blanc p-1.5"
          />
        )}
        <div className="min-w-[240px] flex-1">
          <div className="flex items-center gap-2 rounded-[8px] border border-graphite px-3 py-2">
            <Link2 size={14} strokeWidth={1.5} className="shrink-0 text-fumee" />
            <span className="truncate text-[13px] text-blanc">{parrainage.url}</span>
            <button onClick={() => void copier()} aria-label="Copier le lien" className="ml-auto shrink-0 text-fumee hover:text-blanc">
              {copie ? <Check size={14} strokeWidth={1.5} /> : <Copy size={14} strokeWidth={1.5} />}
            </button>
          </div>
          <p className="mt-2 text-[13px] text-fumee">
            Code à dicter : <span className="text-blanc">{parrainage.code}</span>
          </p>

          <div className="mt-5 flex flex-wrap gap-8">
            <div>
              <p className="label-caps text-fumee">Filleuls</p>
              <p className="mt-1 flex items-center gap-1.5 text-[20px] font-light text-blanc">
                <Users size={15} strokeWidth={1.5} /> {parrainage.filleuls.length}
              </p>
            </div>
            <div>
              <p className="label-caps text-fumee">Dont abonnés</p>
              <p className="mt-1 text-[20px] font-light text-blanc">{abonnes}</p>
            </div>
            <div>
              <p className="label-caps text-fumee">À recevoir</p>
              <p className="text-match-gradient mt-1 text-[20px] font-light">{euros(parrainage.gains.du_centimes)}</p>
            </div>
          </div>
        </div>
      </div>

      {parrainage.gains.lignes.length > 0 && (
        <div className="mt-6 border-t border-graphite pt-5">
          <SectionLabel>Vos commissions</SectionLabel>
          <div className="mt-3 overflow-x-auto">
            <Table head={['Période', 'Rôle', 'Source', 'Montant', 'État']}>
              {parrainage.gains.lignes.slice(0, 12).map((l) => (
                <tr key={l.id} className="border-b border-graphite last:border-0">
                  <td className="py-2.5 pr-4 text-[13px] text-fumee">{dateCourte(l.periode)}</td>
                  <td className="py-2.5 pr-4 text-[13px] text-blanc">{LIBELLE_ROLE[l.role]}</td>
                  <td className="py-2.5 pr-4 text-[13px] text-fumee">
                    {l.entreprise ?? nomDe({ prenom: l.prenom, nom: l.nom })}
                  </td>
                  <td className="py-2.5 pr-4 text-[13px] text-blanc">{euros(l.montant_centimes)}</td>
                  <td className="py-2.5 text-[13px]">
                    <Badge filled={l.statut === 'verse'}>{l.statut === 'verse' ? 'Versée' : 'Due'}</Badge>
                  </td>
                </tr>
              ))}
            </Table>
          </div>
          <p className="mt-3 text-[12px] leading-relaxed text-fumee">
            Les commissions sont enregistrées ici puis versées par virement. Elles ne partent pas
            automatiquement : un humain les valide.
          </p>
        </div>
      )}

      {parrainage.filleuls.length > 0 && (
        <div className="mt-6 border-t border-graphite pt-5">
          <SectionLabel>Qui vous avez amené</SectionLabel>
          <div className="mt-3 flex flex-wrap gap-2">
            {parrainage.filleuls.slice(0, 20).map((f) => (
              <span key={f.filleul_id} className="rounded-full border border-graphite px-3 py-1.5 text-[13px] text-fumee">
                <Gift size={12} strokeWidth={1.5} className="mr-1.5 inline" />
                {f.entreprise ?? nomDe({ prenom: f.prenom, nom: f.nom })}
                {f.abonne && <span className="text-match-gradient ml-1.5">abonné</span>}
              </span>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

/* ————————————————— Classement ————————————————— */

const AXES = [
  { valeur: 'global', texte: 'Score global' },
  { valeur: 'sociale', texte: 'Influence sociale' },
  { valeur: 'reseau', texte: 'Influence réseau' },
  { valeur: 'virtuelle', texte: 'Influence virtuelle' },
] as const;

function Classement({
  classement,
  onChanger,
  secteur,
}: {
  classement: ClassementInfluence | null;
  onChanger: (c: ClassementInfluence | null) => void;
  secteur: string | null;
}) {
  const [axe, setAxe] = useState<string>('global');
  const [monSecteur, setMonSecteur] = useState(false);

  const recharger = async (nouvelAxe: string, filtrer: boolean) => {
    setAxe(nouvelAxe);
    setMonSecteur(filtrer);
    const q = new URLSearchParams({ axe: nouvelAxe, limite: '10' });
    if (filtrer && secteur) q.set('secteur', secteur);
    const r = await appelerApi<ClassementInfluence>(`/api/influence/classement?${q}`);
    onChanger(r.donnees);
  };

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SectionLabel>Le classement</SectionLabel>
        <div className="flex flex-wrap items-center gap-3">
          {secteur && (
            <Btn variant={monSecteur ? 'primary' : 'ghost'} onClick={() => void recharger(axe, !monSecteur)}>
              {secteur}
            </Btn>
          )}
          <Select label="" value={axe} onChange={(v) => void recharger(v, monSecteur)} options={AXES} />
        </div>
      </div>

      <div className="mt-4 flex flex-col">
        {(classement?.classement ?? []).map((r) => (
          <div
            key={r.personne_id}
            className={`flex items-center gap-4 border-b border-graphite py-3 last:border-0 ${r.moi ? 'rounded-[8px] bg-[#262626] px-3' : ''}`}
          >
            <span className={`w-7 shrink-0 font-serif text-[20px] ${r.rang <= 3 ? 'text-match-gradient' : 'text-fumee'}`}>
              {r.rang}
            </span>
            {r.rang === 1 && <Trophy size={15} strokeWidth={1.5} className="shrink-0 text-blanc" />}
            <div className="min-w-0 flex-1">
              <p className="truncate text-[14px] text-blanc">
                {r.entreprise ?? nomDe({ prenom: r.prenom, nom: r.nom })}
                {r.moi && <span className="ml-2 text-[12px] text-fumee">vous</span>}
              </p>
              <p className="truncate text-[12px] text-fumee">{r.secteur ?? '—'}</p>
            </div>
            <span className="shrink-0 font-serif text-[20px] text-blanc">{r.valeur}</span>
          </div>
        ))}
        {classement?.classement.length === 0 && (
          <p className="text-[13px] text-fumee">
            Personne n’a encore de score ici. Renseignez vos chiffres : vous serez le premier.
          </p>
        )}
      </div>
    </Card>
  );
}
